import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getListeningAudioSignedUrl, useUploadAudio } from '../../../shared/hooks/useUploadAudio';
import { parsePastedQuestions } from '../../../shared/qbank/parse-pasted-questions';
import { QuestionEditorPanel } from '../../../shared/qbank/QuestionEditorPanel';
import { Button } from '../../../shared/ui/Button';
import { DesktopToolNote } from '../../../shared/ui/DesktopToolNote';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { Select } from '../../../shared/ui/Select';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { StatusBanner } from '../../../shared/ui/StatusBanner';
import type { Json } from '../../../types/database';
import {
  createQuestion,
  deleteQuestion,
  getSection,
  getTest,
  listPassages,
  listQuestions,
  QBANK_QUERY_KEYS,
  updateQuestion,
  updateSection,
  upsertAnswerKey,
  upsertPassage,
  type QuestionKind,
  type QuestionRow,
  type SectionPassageRow,
  type TestSectionRow,
} from '../tests-api';

import s from './SectionEditorPage.module.css';

function questionKindLabel(kind: QuestionKind): string {
  switch (kind) {
    case 'mcq':
      return 'Multiple choice';
    case 'tfng':
      return 'True / False / Not Given';
    case 'ynng':
      return 'Yes / No / Not Given';
    case 'matching_info':
      return 'Matching info';
    case 'matching_headings':
      return 'Matching headings';
    case 'matching_features':
      return 'Matching features';
    case 'form_completion':
      return 'Form completion';
    case 'note_completion':
      return 'Note completion';
    case 'table_completion':
      return 'Table completion';
    case 'summary_completion':
      return 'Summary completion';
    case 'sentence_completion':
      return 'Sentence completion';
    case 'short_answer':
      return 'Short answer';
  }
}

const Q_KINDS: readonly QuestionKind[] = [
  'mcq',
  'tfng',
  'ynng',
  'short_answer',
  'sentence_completion',
  'note_completion',
  'form_completion',
  'table_completion',
  'summary_completion',
  'matching_info',
  'matching_headings',
  'matching_features',
];

function defaultPayloadFor(kind: QuestionKind): Json {
  switch (kind) {
    case 'mcq':
      return { choices: ['', ''] } as Json;
    case 'short_answer':
    case 'sentence_completion':
    case 'note_completion':
    case 'form_completion':
    case 'table_completion':
    case 'summary_completion':
      return { max_words: 3 } as Json;
    default:
      return {} as Json;
  }
}

export function AdminSectionEditorPage(): JSX.Element {
  const { testId = '', sectionId = '' } = useParams<{
    testId: string;
    sectionId: string;
  }>();
  const queryClient = useQueryClient();

  const sectionQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.section(sectionId),
    queryFn: () => getSection(sectionId),
    enabled: sectionId !== '',
  });
  const testQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.test(testId),
    queryFn: () => getTest(testId),
    enabled: testId !== '',
  });
  const passagesQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.passages(sectionId),
    queryFn: () => listPassages(sectionId),
    enabled: sectionId !== '',
  });
  const questionsQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.questions(sectionId),
    queryFn: () => listQuestions(sectionId),
    enabled: sectionId !== '',
  });

  if (sectionQuery.isPending) {
    // Shaped like the loaded page: crumbs, title block, then the first card.
    return (
      <div className={s.root} role="status" aria-label="Loading section">
        <Skeleton width={200} height={14} />
        <div className={s.titleSkeleton}>
          <Skeleton width={72} height={12} />
          <Skeleton width="55%" height={36} />
          <Skeleton width={140} height={16} />
        </div>
        <Skeleton height={220} radius="xl" />
      </div>
    );
  }
  if (sectionQuery.error !== null || !sectionQuery.data) {
    return (
      <div className={s.root}>
        <StatusBanner tone="error">
          Could not load section: {sectionQuery.error?.message ?? 'not found'}
        </StatusBanner>
      </div>
    );
  }

  const section = sectionQuery.data;

  return (
    <div className={s.root}>
      {/* Label/field rows, Markdown textareas and per-type payload editors are
          honestly desktop-shaped; say so once per tab rather than reflowing them. */}
      <DesktopToolNote id="section-editor" />

      <nav className={s.crumbs} aria-label="Breadcrumbs">
        <Link to="/admin/tests" className={s.crumb}>
          Global tests
        </Link>
        <span aria-hidden="true">/</span>
        <Link to={`/admin/tests/${testId}`} className={s.crumb}>
          {testQuery.data?.title ?? 'test'}
        </Link>
      </nav>

      <PageHeader
        eyebrow={section.kind.toUpperCase()}
        title={
          section.kind === 'listening'
            ? 'Listening section'
            : section.kind === 'reading'
              ? 'Reading section'
              : section.kind === 'writing'
                ? 'Writing section'
                : 'Speaking section'
        }
        subtitle={`${String(section.duration_minutes)} minute section`}
      />

      <SectionMetaForm section={section} queryClient={queryClient} />

      {section.kind === 'listening' ? (
        <ListeningAudioBlock
          sectionId={sectionId}
          passages={passagesQuery.data ?? []}
          isLoading={passagesQuery.isPending}
          onMutated={() =>
            queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.passages(sectionId) })
          }
        />
      ) : null}

      <PassagesBlock
        sectionId={sectionId}
        kind={section.kind}
        passages={passagesQuery.data ?? []}
        isLoading={passagesQuery.isPending}
        onMutated={() =>
          queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.passages(sectionId) })
        }
      />

      <QuestionsBlock
        sectionId={sectionId}
        questions={questionsQuery.data ?? []}
        isLoading={questionsQuery.isPending}
        onMutated={() =>
          queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.questions(sectionId) })
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section metadata: duration + instructions
// ---------------------------------------------------------------------------

interface SectionMetaFormProps {
  section: TestSectionRow;
  queryClient: ReturnType<typeof useQueryClient>;
}

function SectionMetaForm({ section, queryClient }: SectionMetaFormProps): JSX.Element {
  const [instructions, setInstructions] = useState(section.instructions_md ?? '');
  const [duration, setDuration] = useState(String(section.duration_minutes));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setInstructions(section.instructions_md ?? '');
    setDuration(String(section.duration_minutes));
  }, [section.id, section.instructions_md, section.duration_minutes]);

  const mutation = useMutation({
    mutationFn: () =>
      updateSection({
        sectionId: section.id,
        patch: {
          instructions_md: instructions.trim() === '' ? null : instructions,
          duration_minutes: Number.parseInt(duration, 10),
        },
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.section(section.id) });
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const durationNum = Number.parseInt(duration, 10);
  const durationValid = !Number.isNaN(durationNum) && durationNum > 0 && durationNum <= 240;

  return (
    <section className={s.card}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>Section settings</h2>
        {saved ? <span className={s.savedBadge}>Saved</span> : null}
      </div>
      <div className={s.metaRow}>
        <div>
          <Label htmlFor="duration">Duration (min)</Label>
          <Input
            id="duration"
            type="number"
            min={1}
            max={240}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            invalid={!durationValid}
          />
        </div>
        <div className={s.metaFull}>
          <Label htmlFor="instructions">Instructions (Markdown)</Label>
          <textarea
            id="instructions"
            className={s.textarea}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
          />
        </div>
      </div>
      <div className={s.cardActions}>
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!durationValid}
        >
          Save section
        </Button>
      </div>
      {mutation.error !== null ? (
        <StatusBanner tone="error">{mutation.error.message}</StatusBanner>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Listening audio: one upload per section. Stored on the first passage row,
// which we create on demand.
// ---------------------------------------------------------------------------

interface ListeningAudioBlockProps {
  sectionId: string;
  passages: readonly SectionPassageRow[];
  isLoading: boolean;
  onMutated: () => void;
}

function ListeningAudioBlock({
  sectionId,
  passages,
  isLoading,
  onMutated,
}: ListeningAudioBlockProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const upload = useUploadAudio();

  const passage = passages[0] ?? null;

  useEffect(() => {
    if (!passage?.audio_path) {
      setSignedUrl(null);
      return undefined;
    }
    let cancelled = false;
    void getListeningAudioSignedUrl(passage.audio_path)
      .then((url) => {
        if (!cancelled) setSignedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [passage?.audio_path]);

  async function handleFile(file: File): Promise<void> {
    upload.reset();
    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
    const path = `sections/${sectionId}.${ext}`;
    const result = await upload.upload({ path, file });
    await upsertPassage({
      passageId: passage?.id,
      sectionId,
      displayOrder: passage?.display_order ?? 1,
      title: passage?.title ?? null,
      body_md: passage?.body_md ?? null,
      audio_path: result.path,
    });
    onMutated();
  }

  return (
    <section className={s.card}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>Listening audio</h2>
      </div>
      {isLoading ? (
        <p className={s.loading}>Loading…</p>
      ) : (
        <>
          {passage?.audio_path ? (
            <div className={s.audioBlock}>
              <code className={s.audioPath}>{passage.audio_path}</code>
              {signedUrl ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- admin preview of source MP3; captions live in the student-facing player (Phase 16), where transcripts are out of scope for V1
                <audio controls src={signedUrl} className={s.audioPlayer}>
                  Your browser does not support audio playback.
                </audio>
              ) : (
                <p className={s.audioHint}>Generating signed preview URL…</p>
              )}
            </div>
          ) : (
            <p className={s.audioHint}>No audio uploaded yet. Use MP3, M4A or WAV; up to 25 MB.</p>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp4,audio/aac,audio/wav,audio/wave,audio/x-wav"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <div className={s.cardActions}>
            <Button
              type="button"
              variant={passage?.audio_path ? 'secondary' : 'primary'}
              loading={upload.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {passage?.audio_path ? 'Replace audio' : 'Upload audio'}
            </Button>
          </div>
          {upload.error !== null ? (
            <StatusBanner tone="error">{upload.error.message}</StatusBanner>
          ) : null}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Passages: Reading has 3, Listening has 1 (audio above), Writing/Speaking
// usually 0. Keep them as a flat editable list.
// ---------------------------------------------------------------------------

interface PassagesBlockProps {
  sectionId: string;
  kind: TestSectionRow['kind'];
  passages: readonly SectionPassageRow[];
  isLoading: boolean;
  onMutated: () => void;
}

function passageEmptyCopy(kind: TestSectionRow['kind']): string {
  switch (kind) {
    case 'reading':
      return 'Reading sections carry 3 passages. Paste each one below as Markdown; questions link to a passage_id once you create them.';
    case 'listening':
      return 'Listening sections usually have one passage row that holds the audio file (uploaded above). Adding a transcript here is optional.';
    case 'writing':
      return 'Writing tasks live as questions; no passage rows needed.';
    case 'speaking':
      return 'Speaking parts live as questions; no passage rows needed.';
  }
}

function PassagesBlock({
  sectionId,
  kind,
  passages,
  isLoading,
  onMutated,
}: PassagesBlockProps): JSX.Element {
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');

  const addPassage = useMutation({
    mutationFn: () =>
      upsertPassage({
        sectionId,
        displayOrder: passages.length + 1,
        title: draftTitle.trim() === '' ? null : draftTitle.trim(),
        body_md: draftBody.trim() === '' ? null : draftBody,
      }),
    onSuccess: () => {
      setDraftTitle('');
      setDraftBody('');
      onMutated();
    },
  });

  return (
    <section className={s.card}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>Passages</h2>
        <span className={s.sectionMeta}>{passages.length}</span>
      </div>
      {isLoading ? (
        <p className={s.loading}>Loading…</p>
      ) : passages.length === 0 ? (
        <p className={s.audioHint}>{passageEmptyCopy(kind)}</p>
      ) : (
        <ul className={s.passageList}>
          {passages.map((p) => (
            <li key={p.id} className={s.passageRow}>
              <span className={s.passageIndex}>#{p.display_order}</span>
              <div>
                <strong>{p.title ?? 'Untitled passage'}</strong>
                {p.body_md ? <p className={s.passageBody}>{p.body_md.slice(0, 240)}…</p> : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className={s.subForm}>
        <div>
          <Label htmlFor="passage-title">New passage title</Label>
          <Input
            id="passage-title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="The history of paper"
          />
        </div>
        <div className={s.metaFull}>
          <Label htmlFor="passage-body">Passage body (Markdown)</Label>
          <textarea
            id="passage-body"
            className={s.textarea}
            rows={5}
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="Paste the passage text here."
          />
        </div>
        <div className={s.cardActions}>
          <Button
            type="button"
            onClick={() => addPassage.mutate()}
            loading={addPassage.isPending}
            disabled={draftTitle.trim() === '' && draftBody.trim() === ''}
          >
            Add passage
          </Button>
        </div>
        {addPassage.error !== null ? (
          <StatusBanner tone="error">{addPassage.error.message}</StatusBanner>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Questions: list + inline editor per question + add new.
// ---------------------------------------------------------------------------

interface QuestionsBlockProps {
  sectionId: string;
  questions: readonly QuestionRow[];
  isLoading: boolean;
  onMutated: () => void;
}

function QuestionsBlock({
  sectionId,
  questions,
  isLoading,
  onMutated,
}: QuestionsBlockProps): JSX.Element {
  const [newKind, setNewKind] = useState<QuestionKind>('mcq');
  const ordered = useMemo(
    () => questions.slice().sort((a, b) => a.display_order - b.display_order),
    [questions],
  );

  const addQuestion = useMutation({
    mutationFn: () =>
      createQuestion({
        sectionId,
        kind: newKind,
        displayOrder: ordered.length + 1,
        prompt_md: 'New question',
        payload_jsonb: defaultPayloadFor(newKind),
      }),
    onSuccess: () => onMutated(),
  });

  return (
    <section className={s.card}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>Questions</h2>
        <span className={s.sectionMeta}>{ordered.length}</span>
      </div>

      {isLoading ? (
        <p className={s.loading}>Loading…</p>
      ) : ordered.length === 0 ? (
        <p className={s.audioHint}>No questions yet — add your first below.</p>
      ) : (
        <ul className={s.questionList}>
          {ordered.map((q) => (
            <QuestionCard key={q.id} question={q} onMutated={onMutated} />
          ))}
        </ul>
      )}

      <div className={s.subForm}>
        <div className={s.addQuestionRow}>
          <div className={s.metaFull}>
            <Label htmlFor="new-kind">Question type</Label>
            <Select
              id="new-kind"
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as QuestionKind)}
            >
              {Q_KINDS.map((k) => (
                <option key={k} value={k}>
                  {questionKindLabel(k)}
                </option>
              ))}
            </Select>
          </div>
          <Button
            type="button"
            onClick={() => addQuestion.mutate()}
            loading={addQuestion.isPending}
          >
            Add question
          </Button>
        </div>
        {addQuestion.error !== null ? (
          <StatusBanner tone="error">{addQuestion.error.message}</StatusBanner>
        ) : null}

        <PasteQuestionsPanel
          sectionId={sectionId}
          startOrder={ordered.length + 1}
          onMutated={onMutated}
        />
      </div>
    </section>
  );
}

interface PastePanelProps {
  sectionId: string;
  startOrder: number;
  onMutated: () => void;
}

const PASTE_PLACEHOLDER =
  'true false not given\tThe author supports the theory.\ttrue\n' +
  'mcq\tWhich option is correct?\tb\toption a ; option b ; option c\n' +
  'short answer\tName the metal described.\t(the )gold';

function PasteQuestionsPanel({ sectionId, startOrder, onMutated }: PastePanelProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const parsed = useMemo(() => parsePastedQuestions(text), [text]);

  const bulk = useMutation({
    mutationFn: async () => {
      let order = startOrder;
      for (const q of parsed.questions) {
        const created = await createQuestion({
          sectionId,
          kind: q.kind,
          displayOrder: order,
          prompt_md: q.promptMd,
          payload_jsonb: q.kind === 'mcq' ? { choices: q.choices } : defaultPayloadFor(q.kind),
        });
        if (q.answerKey !== '') {
          await upsertAnswerKey({ questionId: created.id, key_pattern: q.answerKey });
        }
        order += 1;
      }
      return parsed.questions.length;
    },
    onSuccess: () => {
      setText('');
      setOpen(false);
      onMutated();
    },
  });

  if (!open) {
    return (
      <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
        Paste questions in bulk
      </Button>
    );
  }

  return (
    <div className={s.pastePanel}>
      <Label htmlFor="paste-questions">Paste questions — one per line, tab-separated</Label>
      <textarea
        id="paste-questions"
        className={s.pasteArea}
        rows={6}
        value={text}
        placeholder={PASTE_PLACEHOLDER}
        onChange={(e) => setText(e.target.value)}
      />
      <p className={s.pasteHint}>
        Columns (tab-separated, paste from a spreadsheet):{' '}
        <code>type → prompt → answer key → choices (;-separated)</code>. The answer key and choices
        are optional; lines starting with <code>#</code> are ignored.
      </p>

      {parsed.errors.length > 0 ? (
        <StatusBanner tone="warning">
          {parsed.errors.length} line(s) will be skipped:
          <ul className={s.pasteErrors}>
            {parsed.errors.slice(0, 6).map((e) => (
              <li key={e.lineNumber}>
                Line {e.lineNumber}: {e.message}
              </li>
            ))}
          </ul>
        </StatusBanner>
      ) : null}

      <div className={s.pasteActions}>
        <Button
          type="button"
          onClick={() => bulk.mutate()}
          loading={bulk.isPending}
          disabled={parsed.questions.length === 0}
        >
          Add {parsed.questions.length} question{parsed.questions.length === 1 ? '' : 's'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={bulk.isPending}
          onClick={() => {
            setOpen(false);
            setText('');
          }}
        >
          Cancel
        </Button>
      </div>
      {bulk.error !== null ? <StatusBanner tone="error">{bulk.error.message}</StatusBanner> : null}
    </div>
  );
}

interface QuestionCardProps {
  question: QuestionRow;
  onMutated: () => void;
}

function QuestionCard({ question, onMutated }: QuestionCardProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(question.prompt_md);
  const [payload, setPayload] = useState<Json>(question.payload_jsonb);
  const [keyPattern, setKeyPattern] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPrompt(question.prompt_md);
    setPayload(question.payload_jsonb);
  }, [question.id, question.prompt_md, question.payload_jsonb]);

  // Auto-clear the "Saved" badge after a short window. Cleanup cancels the
  // timer if the component unmounts or the user saves again mid-window.
  useEffect(() => {
    if (!saved) return undefined;
    const id = window.setTimeout(() => setSaved(false), 2000);
    return () => window.clearTimeout(id);
  }, [saved]);

  const save = useMutation({
    mutationFn: async () => {
      await updateQuestion({
        questionId: question.id,
        patch: { prompt_md: prompt, payload_jsonb: payload },
      });
      if (keyPattern.trim() !== '') {
        await upsertAnswerKey({ questionId: question.id, key_pattern: keyPattern.trim() });
      }
    },
    onSuccess: () => {
      setSaved(true);
      onMutated();
    },
  });

  const del = useMutation({
    mutationFn: () => deleteQuestion(question.id),
    onSuccess: () => onMutated(),
  });

  // Reset the "Saved" badge as soon as the user touches the editor again, so
  // it never lingers on top of a dirty form.
  function dirty<T>(setter: (v: T) => void): (v: T) => void {
    return (v) => {
      if (saved) setSaved(false);
      setter(v);
    };
  }

  const dirtyPrompt = dirty(setPrompt);
  const dirtyPayload = dirty<Json>(setPayload);
  const dirtyKey = dirty(setKeyPattern);

  return (
    <li className={s.questionRow}>
      <button
        type="button"
        className={s.questionHead}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={s.questionIndex}>#{question.display_order}</span>
        <span className={s.questionKindPill}>{questionKindLabel(question.kind)}</span>
        <span className={s.questionPrompt}>{question.prompt_md.slice(0, 80)}</span>
        <span className={s.questionToggle} aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open ? (
        <div className={s.questionBody}>
          <QuestionEditorPanel
            kind={question.kind}
            prompt_md={prompt}
            payload_jsonb={payload}
            key_pattern={keyPattern}
            onPromptChange={dirtyPrompt}
            onPayloadChange={dirtyPayload}
            onKeyPatternChange={dirtyKey}
          />
          <div className={s.questionActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (window.confirm('Delete this question? Cannot be undone.')) del.mutate();
              }}
              loading={del.isPending}
            >
              Delete
            </Button>
            {saved ? (
              <span className={s.savedBadge} role="status" aria-live="polite">
                Saved
              </span>
            ) : null}
            <Button type="button" onClick={() => save.mutate()} loading={save.isPending}>
              Save question
            </Button>
          </div>
          {save.error !== null ? (
            <StatusBanner tone="error">{save.error.message}</StatusBanner>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
